import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { Card } from 'tea-component';
import { history, useDispatch, useIntl } from 'umi';
import useChangeSize from '@intelligence/hooks/useChangeSize';
import SvgIcon from '@/pages/home/components/SvgIcon';
import useSteamHourlyCCU from './useSteamHourlyCCU';
import useChart from '@intelligence/v3/_hooks/chart/useChart';
import { formatKMB } from '@intelligence/v3/utils/number';
import { LINE_CHART_STATIC_OPTION } from '@intelligence/v3/components/ChartComponents/LineChart/constants';
import { DATASOURCE_NAME, DataSourceBubbleInfo } from '@intelligence/components/DataSourceComponents';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { ECHARTSCOLOR } from '@intelligence/utils/echartsConst';
import TipContext from '@home/components/TipContext';
import NumArrow from '@intelligence/pages/TopCharts/components/DetailsPage/NumArrow';
import { getDetailGamePathCommon } from '@intelligence/utils/topchartsUtils';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { KPI_TRENDS_HOURLY_CCU_METRIC } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/const';
import { ISteamHourlyCCUProps } from './type';
import {
  buildFullDayXAxisData,
  calculateDateRange,
  extractHourlyValues,
  findLastValidValueIndex,
  formatHourlyDisplayTime,
  formatYAxisLabel,
  getTickIntervalHours,
  shouldShowXAxisTick,
  X_AXIS_SLOT_COUNT,
} from './utils';
import ChartPointRipple from './ChartPointRipple';
import type { ITodayLastPointMeta } from './type';
import './index.scss';

/** ECharts Tooltip 参数类型 */
interface IEchartsTooltipParams {
  axisValue?: string;
  dataIndex?: number;
  seriesIndex?: number;
  seriesName?: string;
  value?: number | string;
  marker?: string;
}

/** ECharts AxisPointer 参数类型 */
interface IEchartsAxisPointerParams {
  value?: string;
}

// 常量提取
const CHART_HEIGHT = 234;
const TOOLTIP_MIN_WIDTH = 349;

/** 三条趋势线的语义色（复用全局 ECHARTSCOLOR） */
const SERIES_COLORS = [
  ECHARTSCOLOR[0],   // 今天(today)
  ECHARTSCOLOR[2],   // 昨天(yesterday)
  ECHARTSCOLOR[10],  // 上周同一天(last_week)
] as const;

const SteamHourlyCCU: React.FC<ISteamHourlyCCUProps> = (props) => {
  const { gameId, entityType, hourlyCcuRawItem, dateType } = props;
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  // 周粒度或月粒度时隐藏CCU卡片
  if (dateType === 'week' || dateType === 'month') {
    return null;
  }

  const { hourlyCcuData, summaryData, isEmpty } = useSteamHourlyCCU({
    hourlyCcuRawItem,
  });
  const { width: viewportWidth } = useChangeSize();
  const tickIntervalHours = useMemo(
    () => getTickIntervalHours(viewportWidth),
    [viewportWidth],
  );

  // 计算日期范围（useMemo 缓存）
  const dateRange = useMemo(() => {
    if (!hourlyCcuData?.base_date) return null;
    return calculateDateRange(hourlyCcuData.base_date);
  }, [hourlyCcuData?.base_date]);

  // 使用 ref 稳定 dateRange 引用，避免 tooltipFormatter 不必要的重创建
  const dateRangeRef = useRef(dateRange);
  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  const handleDrillDown = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      const btnLogCfg = dbiBtnLogMes.steamHourlyCcuDrillDown;
      reportButtonLog({
        ...btnLogCfg,
        extInfo: `${gameId}: drill down to KPI Trends hour tab`,
      });

      dispatch({
        type: 'dbiTopcharts/setTopchartJumpParam',
        payload: {
          timeType: 'hour',
          activeTab: KPI_TRENDS_HOURLY_CCU_METRIC,
          from: 'overview',
        },
      });

      const pathGame = getDetailGamePathCommon({
        entityType: entityType || '',
        gameId,
        tab: 'stats',
      });

      if (pathGame) {
        history.replace(pathGame);
      }
    },
    [gameId, entityType, dispatch],
  );

  // 准备 ECharts option
  const chartData = useMemo(() => {
    if (!hourlyCcuData || !dateRange) return null;

    const { base_date, today, yesterday, last_week } = hourlyCcuData;
    const { yesterday: yesterdayStr, lastWeek: lastWeekStr } = dateRange;

    const xAxisData = buildFullDayXAxisData().slice(1);

    const todayValues = extractHourlyValues(today).slice(1);
    const todayLastValidIndex = findLastValidValueIndex(todayValues);
    const todayLastValue =
      todayLastValidIndex >= 0 ? todayValues[todayLastValidIndex] : null;
    const todaySeriesName = `${formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_today' })} ${base_date}`;
    const todayData = todayValues.map((value, index) => {
      if (index === todayLastValidIndex && value !== null && value !== undefined) {
        // 末端圆点/涟漪/标签由 ChartPointRipple 渲染
        // symbolSize 设为 11 以匹配可见圆点大小，确保 hover 检测区域正确
        return {
          value,
          symbolSize: 11,
          itemStyle: {
            color: 'transparent',
            borderColor: 'transparent',
            opacity: 0,
          },
        };
      }
      return value;
    });

    const todayLastPoint: ITodayLastPointMeta | null =
      todayLastValue !== null && todayLastValue !== undefined
        ? {
          dataIndex: todayLastValidIndex,
          value: todayLastValue,
          seriesName: todaySeriesName,
          totalSlots: todayValues.length,
        }
        : null;

    // 构建 series：保持 24 点位与 X 轴一一对应，未到时段 API 返回 null 自然断开
    const series = [
      {
        name: todaySeriesName,
        type: 'line',
        color: SERIES_COLORS[0],
        data: todayData,
        connectNulls: false,
        showSymbol: true,
        lineStyle: {
          type: 'solid',
          width: 2,
          color: SERIES_COLORS[0],
        },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: SERIES_COLORS[0],
          borderColor: '#FFFFFF',
          borderWidth: 1,
        },
        emphasis: {
          scale: true,
          symbolSize: 8,
          itemStyle: {
            color: SERIES_COLORS[0],
            borderColor: '#FFFFFF',
            borderWidth: 1,
          },
        },
      },
      {
        name: `${formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_yesterday' })} ${yesterdayStr}`,
        type: 'line',
        color: SERIES_COLORS[1],
        data: extractHourlyValues(yesterday).slice(1),
        connectNulls: false,
        showSymbol: true,
        lineStyle: {
          type: [4, 3],
          width: 2,
          color: SERIES_COLORS[1],
        },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: SERIES_COLORS[1],
          borderColor: '#FFFFFF',
          borderWidth: 1,
        },
        emphasis: {
          scale: true,
          symbolSize: 8,
          lineStyle: {
            color: SERIES_COLORS[1],
          },
          itemStyle: {
            color: SERIES_COLORS[1],
            borderColor: '#FFFFFF',
            borderWidth: 1,
          },
        },
      },
      {
        name: `${formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_same_day_last_week' })} ${lastWeekStr} (${formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_contrast_day' })})`,
        type: 'line',
        color: SERIES_COLORS[2],
        data: extractHourlyValues(last_week).slice(1),
        connectNulls: false,
        showSymbol: true,
        lineStyle: {
          type: [4, 3],
          width: 2,
          color: SERIES_COLORS[2],
        },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: SERIES_COLORS[2],
          borderColor: '#FFFFFF',
          borderWidth: 1,
        },
        emphasis: {
          scale: true,
          symbolSize: 8,
          lineStyle: {
            color: SERIES_COLORS[2],
          },
          itemStyle: {
            color: SERIES_COLORS[2],
            borderColor: '#FFFFFF',
            borderWidth: 1,
          },
        },
      },
    ];

    return {
      xAxisData,
      series,
      baseDate: base_date,
      todayLastPoint,
    };
  }, [hourlyCcuData, dateRange, formatMessage]);

  const todayLastPoint = chartData?.todayLastPoint ?? null;

  const xAxisTickCount = chartData?.xAxisData?.length || X_AXIS_SLOT_COUNT;

  // 自定义 tooltip，显示完整日期
  const tooltipFormatter = useCallback(
    (params: IEchartsTooltipParams[]) => {
      const currentDateRange = dateRangeRef.current;
      if (!Array.isArray(params) || params.length === 0 || !currentDateRange) return '';

      const time = params[0]?.axisValue || '';
      const dataIndex = params[0]?.dataIndex;
      const { today: todayStr } = currentDateRange;
      const timeLabel = formatHourlyDisplayTime(time, dataIndex, xAxisTickCount);

      // 使用数组拼接提高可读性和性能
      const tooltipLines: string[] = [];
      
      tooltipLines.push(
        `<div style="min-width: ${TOOLTIP_MIN_WIDTH}px; padding: 16px; display: flex; flex-direction: column; gap: 10px;">`,
        `<div style="display: flex; justify-content: space-between; align-items: center; color: rgba(216, 218, 228, 1); font-weight: 500; font-size: 13px; margin-bottom: 2px;">`,
        `<span>${todayStr} ${timeLabel} UTC+8</span>`,
        '<span>CCU</span>',
        '</div>'
      );

      params.forEach((p: IEchartsTooltipParams) => {
        const seriesColor =
          SERIES_COLORS[p?.seriesIndex as number] || SERIES_COLORS[0];
        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:10px;height:10px;background-color:${seriesColor};"></span>`;
        const seriesName = p?.seriesName || '';
        const value = p?.value;
        const formattedValue = value != null ? formatKMB(value) : '-';

        tooltipLines.push(
          `<div style="display: flex; justify-content: space-between; align-items: center;">`,
          `<span style="display: flex; align-items: center; gap: 6px; color: rgba(235, 236, 241, 1); font-weight: 400; font-size: 12px;">${marker} ${seriesName}</span>`,
          `<span style="color: rgba(235, 236, 241, 1); font-weight: 700; font-size: 14px; margin-left: 20px;">${formattedValue}</span>`,
          '</div>'
        );
      });

      tooltipLines.push('</div>');
      return tooltipLines.join('');
    },
    [xAxisTickCount], 
  );

  const axisPointerLabelFormatter = useCallback(
    (params: IEchartsAxisPointerParams): string =>
      formatHourlyDisplayTime(params?.value || ''),
    [],
  );

  const xAxisLabelInterval = useCallback(
    (index: number): boolean =>
      shouldShowXAxisTick(index, xAxisTickCount, tickIntervalHours),
    [xAxisTickCount, tickIntervalHours],
  );

  // 按间隔显示刻度（不含 00:00），末位格式化为 23:59
  const xAxisLabelFormatter = useCallback(
    (value: string, index: number): string =>
      formatHourlyDisplayTime(value, index, xAxisTickCount),
    [xAxisTickCount],
  );

  // 自定义 Y 轴格式化
  const yAxisLabelFormatter = useCallback(formatYAxisLabel, []);

  // 构建 ECharts option
  const chartOption = useMemo(() => {
    if (!chartData) return LINE_CHART_STATIC_OPTION;

    const { xAxisData, series } = chartData;

    return {
      ...LINE_CHART_STATIC_OPTION,
      animation: false,
      tooltip: {
        trigger: 'axis', // 确保同个时间点所有系列都显示 tooltip
        ...LINE_CHART_STATIC_OPTION.tooltip,
        formatter: tooltipFormatter,
        backgroundColor: 'rgba(38, 40, 50, 0.85)',
        borderWidth: 0,
        borderRadius: 4,
        padding: [0, 0],
        confine: true,
        axisPointer: {
          type: 'line',
          z: 0,
          snap: true, // 吸附到最近的数据点
          lineStyle: {
            type: 'solid',
            color: '#BBBDCB',
            width: 1,
          },
          label: {
            show: true,
            color: '#3B3D4B',
            fontSize: 12,
            fontWeight: 700,
            backgroundColor: '#fff',
            padding: [0, 0, 0, 0],
            margin: 9,
            formatter: axisPointerLabelFormatter,
          },
        },
      },
      grid: {
        ...LINE_CHART_STATIC_OPTION.grid,
        left: 0,
        right: 0,
        bottom: 40,
        containLabel: true,
      },
      xAxis: [
        {
          ...LINE_CHART_STATIC_OPTION.xAxis?.[0],
          data: xAxisData,
          axisLine: {
            lineStyle: {
              color: '#D8DAE4',
            },
          },
          axisTick: {
            show: true,
            alignWithLabel: true,
            interval: xAxisLabelInterval,
            lineStyle: {
              color: '#D8DAE4',
            },
          },
          axisLabel: {
            interval: xAxisLabelInterval,
            formatter: xAxisLabelFormatter,
            rotate: 0,
            color: '#838697',
            fontWeight: 400,
            fontSize: 12,
            margin: 9,
          },
          axisPointer: {
            label: {
              formatter: axisPointerLabelFormatter,
            },
          },
          boundaryGap: true,
        },
      ],
      yAxis: [
        {
          ...LINE_CHART_STATIC_OPTION.yAxis?.[0],
          axisLabel: {
            formatter: yAxisLabelFormatter,
            color: '#838697',
            fontWeight: 400,
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#D8DAE4',
            },
          },
        },
      ],
      series,
      legend: {
        ...LINE_CHART_STATIC_OPTION.legend,
        // 为图例每项单独设置 itemStyle，确保图例图标使用实心原色
        data: series.map((s, index) => ({
          name: s.name,
          icon: 'circle',
          itemStyle: {
            color: SERIES_COLORS[index] || SERIES_COLORS[0],
            borderWidth: 0,
          },
          // 确保置灰后圆点大小保持一致
          inactiveItemStyle: {
            color: '#CCC',
            borderWidth: 0,
            opacity: 0.5,
          },
        })),
        bottom: 0,
        left: 'center',
        itemGap: 20, // 图例项之间的间距
        itemWidth: 8,
        itemHeight: 8,
        textStyle: {
          fontWeight: 400,
          fontSize: 12,
          color: '#838697',
          padding: [0, 0, 0, 0],
        },
        inactiveColor: '#CCC',
        inactiveBorderWidth: 0,
      },
    };
  }, [
    chartData,
    tooltipFormatter,
    axisPointerLabelFormatter,
    xAxisLabelFormatter,
    xAxisLabelInterval,
    yAxisLabelFormatter,
  ]);

  // 使用 useChart hook
  const { chartNode, chartInstance, setOption } = useChart({
    locale: 'en-US',
    initialOption: LINE_CHART_STATIC_OPTION,
  });

  // 更新 option
  useEffect(() => {
    if (!chartInstance.current) return;
    setOption(chartOption);
  }, [chartOption, chartInstance, setOption]);

  // 无数据时隐藏整个模块（确保所有 Hooks 都已执行）
  if (isEmpty) return null;

  return (
    <div className="steam-hourly-ccu">
      <Card>
        <Card.Body
          title={
            <div className="steam-hourly-ccu__header">
              <div className="steam-hourly-ccu__title-wrap">
                <span className="steam-hourly-ccu__title">
                  {formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu' })}
                </span>
                <TipContext
                  tipText={
                    <>
                      {formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_info' })}
                      <br />
                      <DataSourceBubbleInfo
                        dataOfDom={formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu' })}
                        comesFormDom={
                          <>
                            <img src={getDataSourceIcon('steam')} alt="" style={{ marginRight: '4px' }} />
                            {DATASOURCE_NAME.steam}
                          </>
                        }
                        dataSource="source_steam"
                      />
                    </>
                  }
                  className="steam-hourly-ccu__info-icon"
                  AWidth={400}
                />
                <SvgIcon
                  className="steam-hourly-ccu__drill-arrow context-title-arrow"
                  type="arrow-right"
                  onClick={handleDrillDown}
                />
              </div>
              <span className="steam-hourly-ccu__date">
                {summaryData?.date || '-'}
              </span>
            </div>
          }
        >
          <div className="steam-hourly-ccu__stats">
            <div className="steam-hourly-ccu__stat-value--large">
              {summaryData?.value != null ? formatKMB(summaryData.value) : '-'}
            </div>
            <div className="steam-hourly-ccu__stat-trends">
              <div className="steam-hourly-ccu__stat-trend-item">
                <span className="steam-hourly-ccu__stat-trend-label">
                  {formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_dod' })}
                </span>
                {summaryData?.value_dod != null && (
                  <NumArrow 
                    dataContent={{
                      numContent: (summaryData?.dod || '').replace(/^-/, ''),
                      numSize: Number(summaryData.value_dod) > 0 ? 'big' : Number(summaryData.value_dod) < 0 ? 'small' : 'zero',
                      numColor: Number(summaryData.value_dod) > 0 ? 'successColor' : Number(summaryData.value_dod) < 0 ? 'errorColor' : 'grayColor',
                      arrow: true,
                      arrowColor: Number(summaryData.value_dod) > 0 ? 'trendUp' : Number(summaryData.value_dod) < 0 ? 'trendDown' : 'grayUp',
                      isShowZero: true,
                    }}
                    type="num"
                  />
                )}
              </div>
              <div className="steam-hourly-ccu__stat-trend-item">
                <span className="steam-hourly-ccu__stat-trend-label">
                  {formatMessage({ id: 'dbi_top_kpiOverview_steam_hourly_ccu_sdlw' })}
                </span>
                {summaryData?.value_sdlw != null && (
                  <NumArrow 
                    dataContent={{
                      numContent: (summaryData?.sdlw || '').replace(/^-/, ''),
                      numSize: Number(summaryData.value_sdlw) > 0 ? 'big' : Number(summaryData.value_sdlw) < 0 ? 'small' : 'zero',
                      numColor: Number(summaryData.value_sdlw) > 0 ? 'successColor' : Number(summaryData.value_sdlw) < 0 ? 'errorColor' : 'grayColor',
                      arrow: true,
                      arrowColor: Number(summaryData.value_sdlw) > 0 ? 'trendUp' : Number(summaryData.value_sdlw) < 0 ? 'trendDown' : 'grayUp',
                      isShowZero: true,
                    }}
                    type="num"
                  />
                )}
              </div>
            </div>
          </div>
          <div className="steam-hourly-ccu__chart" style={{ position: 'relative' }}>
            <div
              ref={chartNode}
              style={{ width: '100%', height: CHART_HEIGHT }}
            />
            <ChartPointRipple
              chartInstance={chartInstance}
              point={todayLastPoint}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SteamHourlyCCU;
